import { useContext, useMemo, useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { ActionContext } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/ActionContext';

interface Props {
	text: string;
	isHovered: boolean;
}

export const CopyAction = ({ text, isHovered: isCellHovered }: Props) => {
	const { isActionOpen } = useContext(ActionContext);
	const [isIconHovered, setIsIconHovered] = useState(false);
	const [isClicked, setIsClicked] = useState(false);

	const { color, background, preserveLayout } = useMemo(() => {
		const preserveLayout = !isCellHovered && isActionOpen;

		let color: string;
		if (isClicked) {
			color = defaultTheme.v3.colors.dark[8];
		} else if (isIconHovered) {
			color = defaultTheme.v3.colors.blue[6];
		} else {
			color = defaultTheme.v3.colors.dark[0];
		}

		let background: string;
		if (isClicked) {
			background = defaultTheme.v3.colors.gray[4];
		} else if (isIconHovered) {
			background = defaultTheme.v3.colors.gray[2];
		} else {
			background = 'none';
		}

		return { color, background, preserveLayout };
	}, [isClicked, isIconHovered, isCellHovered, isActionOpen]);

	if (!isCellHovered && !isActionOpen) {
		return null;
	}

	return (
		<div
			role="button"
			onClick={async (e) => {
				e.stopPropagation();
				if (!navigator.clipboard?.writeText) {
					return;
				}
				try {
					await navigator.clipboard.writeText(text);
					setIsClicked(true);
				} catch (error) {
					// eslint-disable-next-line no-console
					console.error('Failed to copy to clipboard', error);
				}
			}}
			onMouseEnter={() => setIsIconHovered(true)}
			onMouseLeave={() => {
				setIsIconHovered(false);
				setIsClicked(false);
			}}
			style={{
				display: 'flex',
				alignItems: 'center',
				cursor: 'pointer',
				background,
				borderRadius: '0.25rem',
				padding: '0.125rem',
				...(preserveLayout ? { visibility: 'hidden' as const } : {}),
			}}
		>
			<Icon variant="copy" size="lg" color={color} fillStroke />
		</div>
	);
};
